import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useTranslation } from '../../hooks/useTranslation';

// Google logo SVG
const GoogleLogo = () => (
  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// Microsoft logo SVG
const MicrosoftLogo = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="7.5" height="7.5" fill="#F25022" />
    <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7FBA00" />
    <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00A4EF" />
    <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#FFB900" />
  </svg>
);

function Auth() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const { login, signup, error, setError, resetPassword, googleSignIn, googleSignInWithPopup, microsoftSignIn, microsoftSignInWithPopup, currentUser, resendVerificationEmail } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const t = useTranslation();
  
  // Login form refs
  const loginEmailRef = useRef();
  const loginPasswordRef = useRef();
  
  // Signup form refs
  const signupNameRef = useRef();
  const signupEmailRef = useRef();
  const signupPasswordRef = useRef();
  const signupConfirmPasswordRef = useRef();
  
  // Reset password ref
  const resetEmailRef = useRef();
  const [showResetForm, setShowResetForm] = useState(false);
  
  // Handle login form submission
  async function handleLogin(e) {
    e.preventDefault();
    
    // Clear previous messages
    setMessage({ type: '', text: '' });
    setError('');
    
    const email = loginEmailRef.current.value;
    const password = loginPasswordRef.current.value;
    
    try {
      setIsAuthenticating(true);
      // Login successful - AppContent will handle redirection based on verification status
      await login(email, password); 
    } catch (error) {
      // Error is handled by AuthContext
      console.error("Login error:", error);
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Handle Google sign-in
  async function handleGoogleSignIn() {
    // Clear previous messages
    setMessage({ type: '', text: '' });
    setError('');
    
    try {
      setIsAuthenticating(true);
      console.log('Initiating Google sign-in with popup');
      // Try popup first since we're using original Firebase domain locally
      await googleSignInWithPopup();
      console.log('Google sign-in with popup completed');
    } catch (error) {
      console.error("Google sign-in error:", error);
      // Only show error if it's not due to user closing the popup
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        setMessage({ 
          type: 'error', 
          text: error.code === 'auth/account-exists-with-different-credential' 
            ? 'An account with this email already exists using a different sign-in method.' 
            : 'Failed to sign in with Google. Please try again.' 
        });
      }
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Handle Microsoft sign-in
  async function handleMicrosoftSignIn() {
    // Clear previous messages
    setMessage({ type: '', text: '' });
    setError('');
    
    try {
      setIsAuthenticating(true);
      console.log('Initiating Microsoft sign-in with popup');
      // Try popup sign-in first
      try {
        await microsoftSignInWithPopup();
        console.log('Microsoft sign-in with popup completed');
      } catch (popupError) {
        // If popup fails, fall back to redirect
        console.log('Popup failed, trying redirect method', popupError);
        await microsoftSignIn();
        console.log('Microsoft sign-in redirect initiated');
      }
    } catch (error) {
      console.error("Microsoft sign-in error:", error);
      // Only show error if it's not due to user closing the popup
      if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        setMessage({ 
          type: 'error', 
          text: error.code === 'auth/account-exists-with-different-credential' 
            ? 'An account with this email already exists using a different sign-in method.' 
            : 'Failed to sign in with Microsoft. Please try again.' 
        });
      }
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Handle signup form submission
  async function handleSignup(e) {
    e.preventDefault();
    
    // Clear previous messages
    setMessage({ type: '', text: '' });
    setError('');
    
    const name = signupNameRef.current.value;
    const email = signupEmailRef.current.value;
    const password = signupPasswordRef.current.value;
    const confirmPassword = signupConfirmPasswordRef.current.value;
    
    // Validate passwords match
    if (password !== confirmPassword) {
      return setMessage({ 
        type: 'error', 
        text: t('errors.passwordMatch') 
      });
    }
    
    // Validate password requirements
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      // More concise error message
      return setMessage({
        type: 'error',
        text: t('errors.passwordRequirements')
      });
    }
    
    try {
      setIsAuthenticating(true);
      // Signup successful - AppContent will handle showing the verification page
      await signup(email, password, name);
    } catch (error) {
      // Error is handled by AuthContext
      console.error("Signup error:", error);
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Function to validate password strength
  function validatePassword(password) {
    const errors = [];
    
    if (password.length < 10) {
      errors.push(t('errors.passwordMinLength'));
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push(t('errors.passwordUppercase'));
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push(t('errors.passwordLowercase'));
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push(t('errors.passwordNumber'));
    }
    
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push(t('errors.passwordSpecial'));
    }
    
    return errors;
  }
  
  // Handle password reset
  async function handlePasswordReset(e) {
    e.preventDefault();
    
    // Clear previous messages
    setMessage({ type: '', text: '' });
    setError('');
    
    const email = resetEmailRef.current.value;
    
    try {
      setIsAuthenticating(true);
      await resetPassword(email);
      setMessage({ 
        type: 'success', 
        text: t('auth.passwordResetEmailSent') 
      });
    } catch (error) {
      // Error is handled by AuthContext
      console.error("Password reset error:", error);
    } finally {
      setIsAuthenticating(false);
    }
  }
  
  // Toggle between login and signup
  function switchTab(tab) {
    setActiveTab(tab);
    setError('');
    setMessage({ type: '', text: '' });
    setShowResetForm(false);
  }
  
  // Toggle password reset form
  function toggleResetForm() {
    setShowResetForm(!showResetForm);
    setError('');
    setMessage({ type: '', text: '' });
  }

  return (
    <div className="min-h-screen flex items-start justify-center bg-gray-100 dark:bg-gray-900 pt-16">
      <div className="max-w-md w-full mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        {/* Controls section for theme and language */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <img 
              src={theme === 'dark' ? "/Sanction_Screener_dark.png" : "/Sanction_Screener_light.png"} 
              alt="Sanction Screener" 
              className="h-10 transition-all duration-300 ease-in-out"
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              onClick={toggleTheme}
              title={theme === 'dark' ? t('auth.switchToLightMode') : t('auth.switchToDarkMode')}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <LanguageSwitcher />
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-300 dark:border-gray-600 mb-6">
          <div 
            className={`py-2 px-4 cursor-pointer ${activeTab === 'login' 
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 font-medium' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
            onClick={() => switchTab('login')}
          >
            {t('auth.login')}
          </div>
          <div 
            className={`py-2 px-4 cursor-pointer ${activeTab === 'signup' 
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 font-medium' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
            onClick={() => switchTab('signup')}
          >
            {t('auth.signup')}
          </div>
        </div>
        
        {/* Display error or success message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-md">
            {error}
          </div>
        )}
        
        {message.text && (
          <div className={`mb-4 p-3 rounded-md ${
            message.type === 'error' 
              ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300' 
              : message.type === 'warning' 
                ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300' 
                : 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
          }`}>
            {message.text}
          </div>
        )}
        
        {/* Login Form */}
        {activeTab === 'login' && !showResetForm && (
          <>
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{t('auth.welcomeBack')}</h2>
              <p className="text-gray-600 dark:text-gray-400">{t('auth.loginNow')}</p>
            </div>
            
            <form className="space-y-4" onSubmit={handleLogin}>
              <div className="space-y-1">
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.email')}
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.enterEmail')}
                  required
                  ref={loginEmailRef}
                />
              </div>
              
              <div className="space-y-1">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.password')}
                </label>
                <input
                  id="login-password"
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.enterPassword')}
                  required
                  ref={loginPasswordRef}
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isAuthenticating}
              >
                {isAuthenticating ? t('common.loading') : t('auth.login')}
              </button>
            </form>

            <div className="relative flex items-center justify-center my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative px-4 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">
                {t('auth.or')}
              </div>
            </div>
            
            <div className="space-y-3 flex flex-col items-center">
              {/* Google Sign-in Button - Updated styling */}
              <button 
                className="w-64 flex items-center justify-center h-[41px] px-4 border border-gray-300 dark:border-[#8E918F] rounded-md shadow-sm text-sm font-medium transition-colors duration-200
                bg-white text-[#1F1F1F] hover:bg-gray-50 
                dark:bg-[#131314] dark:text-[#E3E3E3] dark:hover:bg-[#1f1f20] 
                disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGoogleSignIn} 
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  t('common.loading')
                ) : (
                  <div className="flex items-center w-full justify-center">
                    {/* Google Logo - Padding adjusted slightly via margin */}
                    <span className="mr-2.5"> {/* Adjusted from mr-2 based on guidelines */}
                      <GoogleLogo />
                    </span>
                    <span>{t('auth.continueWithGoogle')}</span>
                  </div>
                )}
              </button>
              
              {/* Microsoft Sign-in Button - Updated styling */}
              <button 
                className="w-64 flex items-center justify-center h-[41px] px-4 border rounded-sm shadow-sm font-semibold text-sm transition-colors duration-200
                bg-white text-[#5E5E5E] hover:bg-gray-50 border-[#8C8C8C]
                dark:bg-[#2F2F2F] dark:text-[#FFFFFF] dark:border-[#2F2F2F] dark:hover:bg-[#3d3d3d] 
                disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleMicrosoftSignIn} 
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  t('common.loading')
                ) : (
                  <div className="flex items-center w-full justify-center">
                    {/* Microsoft Logo with exact padding */}
                    <span className="flex-shrink-0 pl-[12px]">
                      <MicrosoftLogo />
                    </span>
                    <span className="pl-[12px] pr-[12px]">{t('auth.continueWithMicrosoft')}</span>
                  </div>
                )}
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <button 
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 hover:underline"
                onClick={toggleResetForm}
              >
                {t('auth.forgotPassword')}
              </button>
            </div>
          </>
        )}
        
        {/* Reset Password Form */}
        {activeTab === 'login' && showResetForm && (
          <>
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{t('auth.resetPassword')}</h2>
              <p className="text-gray-600 dark:text-gray-400">{t('auth.resetPasswordPrompt')}</p>
            </div>
            
            <form className="space-y-4" onSubmit={handlePasswordReset}>
              <div className="space-y-1">
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.email')}
                </label>
                <input
                  id="reset-email"
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.enterEmail')}
                  required
                  ref={resetEmailRef}
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isAuthenticating}
              >
                {isAuthenticating ? t('common.processing') : t('auth.sendResetLink')}
              </button>
              
              <button 
                type="button" 
                className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium
                bg-transparent hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors duration-200"
                onClick={toggleResetForm}
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          </>
        )}
        
        {/* Signup Form */}
        {activeTab === 'signup' && (
          <>
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{t('auth.createAccount')}</h2>
              <p className="text-gray-600 dark:text-gray-400">{t('auth.signup')}</p>
            </div>
            
            <form className="space-y-4" onSubmit={handleSignup}>
              <div className="space-y-1">
                <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.name')}
                </label>
                <input
                  id="signup-name"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.enterName')}
                  required
                  ref={signupNameRef}
                />
              </div>
              
              <div className="space-y-1">
                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.email')}
                </label>
                <input
                  id="signup-email"
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.enterEmail')}
                  required
                  ref={signupEmailRef}
                />
              </div>
              
              <div className="space-y-1">
                <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.password')}
                </label>
                <input
                  id="signup-password"
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.enterPassword')}
                  required
                  ref={signupPasswordRef}
                />
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('errors.passwordRequirements')} {t('errors.passwordMinLength')}, {t('errors.passwordUppercase')}, {t('errors.passwordLowercase')}, {t('errors.passwordNumber')}, {t('errors.passwordSpecial')}
                </div>
              </div>
              
              <div className="space-y-1">
                <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  id="signup-confirm-password"
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.confirmYourPassword')}
                  required
                  ref={signupConfirmPasswordRef}
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isAuthenticating}
              >
                {isAuthenticating ? t('common.processing') : t('auth.createAccount')}
              </button>
            </form>

            <div className="relative flex items-center justify-center my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative px-4 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">
                {t('auth.or')}
              </div>
            </div>
            
            <div className="space-y-3 flex flex-col items-center">
              {/* Google Sign-in Button - Updated styling (Signup form) */}
              <button 
                 className="w-64 flex items-center justify-center h-[41px] px-4 border border-gray-300 dark:border-[#8E918F] rounded-md shadow-sm text-sm font-medium transition-colors duration-200
                 bg-white text-[#1F1F1F] hover:bg-gray-50 
                 dark:bg-[#131314] dark:text-[#E3E3E3] dark:hover:bg-[#1f1f20] 
                 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGoogleSignIn} 
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  t('common.loading')
                ) : (
                  <div className="flex items-center w-full justify-center">
                    {/* Google Logo - Padding adjusted slightly via margin */}
                    <span className="mr-2.5"> {/* Adjusted from mr-2 */}
                      <GoogleLogo />
                    </span>
                    <span>{t('auth.continueWithGoogle')}</span>
                  </div>
                )}
              </button>
              
              {/* Microsoft Sign-in Button - Updated styling (Signup form) */}
              <button 
                className="w-64 flex items-center justify-center h-[41px] px-4 border rounded-sm shadow-sm font-semibold text-sm transition-colors duration-200
                bg-white text-[#5E5E5E] hover:bg-gray-50 border-[#8C8C8C]
                dark:bg-[#2F2F2F] dark:text-[#FFFFFF] dark:border-[#2F2F2F] dark:hover:bg-[#3d3d3d] 
                disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleMicrosoftSignIn} 
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  t('common.loading')
                ) : (
                  <div className="flex items-center w-full justify-center">
                    {/* Microsoft Logo with exact padding */}
                    <span className="flex-shrink-0 pl-[12px]">
                      <MicrosoftLogo />
                    </span>
                    <span className="pl-[12px] pr-[12px]">{t('auth.continueWithMicrosoft')}</span>
                  </div>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Auth;